import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { Button } from "./components";
import { useEsmAccess } from "./FeatureAccess";
import {
  helpActions,
  visibleHelpGuides,
  helpTopics,
  quickStartGuides,
  searchHelp,
  type HelpActionId,
} from "./help-content";

type Topic = "all" | (typeof helpTopics)[number]["id"];
export function Help({
  onNavigate,
}: {
  onNavigate: (action: HelpActionId) => void;
}) {
  const helpGuides = visibleHelpGuides(useEsmAccess());
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState<Topic>("all");
  // Contextual "?" help links straight to a guide with ?guide=<id>.
  const [selectedId, setSelected] = useState<string | undefined>(() => {
    try {
      return new URLSearchParams(location.search).get("guide") || undefined;
    } catch {
      return undefined;
    }
  });
  const heading = useRef<HTMLHeadingElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const cardToRestore = useRef<string | undefined>(undefined);
  const selected = helpGuides.find((guide) => guide.id === selectedId);
  const results = searchHelp(query, topic, helpGuides);
  const matches = searchHelp(query, "all", helpGuides);
  useEffect(() => {
    if (!selectedId) return;
    heading.current?.focus();
    heading.current?.scrollIntoView({ block: "start" });
  }, [selectedId]);
  const openGuide = (id: string) => {
    cardToRestore.current = id;
    setSelected(id);
  };
  const back = () => {
    setSelected(undefined);
    requestAnimationFrame(() => {
      const card = document.getElementById(
        `help-card-${cardToRestore.current}`,
      );
      (card || search.current)?.focus();
    });
  };
  const reset = () => {
    setQuery("");
    setTopic("all");
    setSelected(undefined);
    search.current?.focus();
  };

  return (
    <div className="help-center">
      <header className="help-intro">
        <div>
          <span className="eyebrow">PROOF STUDIO HANDBOOK</span>
          <h1>Guides & help</h1>
          <p>
            Build the form, shape the document, and deliver the right output.
          </p>
          <p>
            <a href="/docs">Public API integration guide</a> ·{" "}
            <a href="/docs/scalar">Scalar API reference</a>
          </p>
        </div>
        <div className="help-search">
          <Search size={18} aria-hidden="true" />
          <input
            ref={search}
            aria-label="Search guides"
            placeholder="Search features, tasks or errors…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelected(undefined);
            }}
          />
          {query && (
            <button
              className="icon-button"
              aria-label="Clear guide search"
              onClick={() => {
                setQuery("");
                search.current?.focus();
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </header>
      {!selected && !query.trim() && topic === "all" && (
        <section className="help-starts" aria-label="Quick starts">
          {quickStartGuides.map((id, index) => {
            const guide = helpGuides.find((entry) => entry.id === id)!;
            return (
              <button key={id} onClick={() => openGuide(id)}>
                <span className="help-start-number">0{index + 1}</span>
                <strong>
                  {
                    [
                      "Create a project",
                      "Bring in existing artwork",
                      "Generate from a spreadsheet",
                    ][index]
                  }
                </strong>
                <span>{guide.summary}</span>
                <ArrowUpRight size={18} aria-hidden="true" />
              </button>
            );
          })}
        </section>
      )}
      <div className="help-layout">
        <aside className="help-sidebar">
          <h2>Browse topics</h2>
          <nav aria-label="Guide topics" className="help-topics">
            <button
              aria-pressed={topic === "all"}
              onClick={() => {
                setTopic("all");
                setSelected(undefined);
              }}
            >
              <span>All guides</span>
              <span aria-hidden="true">{matches.length}</span>
            </button>
            {helpTopics.map((item) => (
              <button
                key={item.id}
                aria-pressed={topic === item.id}
                onClick={() => {
                  setTopic(item.id);
                  setSelected(undefined);
                }}
              >
                <span>{item.label}</span>
                <span aria-hidden="true">
                  {matches.filter((guide) => guide.topic === item.id).length}
                </span>
              </button>
            ))}
          </nav>
          <p className="help-scope-note">
            Written for the current demo. Guides identify where a production
            host, persistent storage or print qualification is required.
          </p>
        </aside>
        <main className="help-content">
          {selected ? (
            <>
              <button className="text-button help-back" onClick={back}>
                <ArrowLeft size={15} /> Back to guides
              </button>
              <article
                className="help-article panel"
                aria-labelledby="help-guide-title"
              >
                <div className="help-article-topic">
                  <BookOpen size={16} aria-hidden="true" />{" "}
                  {helpTopics.find((item) => item.id === selected.topic)!.label}
                </div>
                <h2 id="help-guide-title" ref={heading} tabIndex={-1}>
                  {selected.title}
                </h2>
                <p className="help-summary">{selected.summary}</p>
                <ol className="help-steps">
                  {selected.steps.map(([title, body], index) => (
                    <li key={title}>
                      <span aria-hidden="true">{index + 1}</span>
                      <div>
                        <h3>{title}</h3>
                        <p>{body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                {!!selected.options?.length && (
                  <section
                    className="help-options"
                    aria-label="Options and details"
                  >
                    <h3>Options & details</h3>
                    <dl>
                      {selected.options.map(([name, description]) => (
                        <div key={name}>
                          <dt>{name}</dt>
                          <dd>{description}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}
                {!!selected.notes?.length && (
                  <section
                    className="help-notes"
                    aria-label="Before you continue"
                  >
                    <h3>Before you continue</h3>
                    {selected.notes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                  </section>
                )}
                <div
                  className="help-actions"
                  aria-label="Open a workspace tool"
                >
                  {selected.actions.map((id, index) => (
                    <Button
                      key={id}
                      kind={index === 0 ? "primary" : ""}
                      onClick={() => onNavigate(id)}
                    >
                      {helpActions[id].label}
                      <ArrowUpRight size={14} aria-hidden="true" />
                    </Button>
                  ))}
                </div>
              </article>
              <section className="help-related" aria-label="Related guides">
                <h2>Continue learning</h2>
                {selected.related.map((id) => {
                  const guide = helpGuides.find((entry) => entry.id === id)!;
                  return (
                    <button key={id} onClick={() => openGuide(id)}>
                      <span>
                        <strong>{guide.title}</strong>
                        <span>{guide.summary}</span>
                      </span>
                      <ChevronRight size={17} aria-hidden="true" />
                    </button>
                  );
                })}
              </section>
            </>
          ) : (
            <>
              <div className="help-results-heading">
                <h2>
                  {topic === "all"
                    ? "Explore the handbook"
                    : helpTopics.find((item) => item.id === topic)!.label}
                </h2>
                <span role="status" aria-live="polite">
                  {results.length} {results.length === 1 ? "guide" : "guides"}
                  {query.trim() ? " found" : ""}
                </span>
              </div>
              {results.length ? (
                <div className="help-guide-grid">
                  {results.map((guide) => (
                    <button
                      id={`help-card-${guide.id}`}
                      className="help-guide-card panel"
                      key={guide.id}
                      onClick={() => openGuide(guide.id)}
                    >
                      <span className="help-guide-topic">
                        {
                          helpTopics.find((item) => item.id === guide.topic)!
                            .label
                        }
                      </span>
                      <h3>{guide.title}</h3>
                      <p>{guide.summary}</p>
                      <span className="help-read">
                        Read guide <ChevronRight size={14} aria-hidden="true" />
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <section className="help-empty panel">
                  <Search size={26} aria-hidden="true" />
                  <h3>No guides match your search</h3>
                  <p>
                    Try a feature name such as “bulk template,” “document
                    frame,” “managed fonts” or “CMYK,” or clear the filters.
                  </p>
                  <Button onClick={reset}>Clear search and filters</Button>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
